/**
 * Centralized calendar practitioner access resolver.
 *
 * UI selection is filtered by this model; FHIR AccessPolicy remains authoritative.
 */
import type { Practitioner } from '@medplum/fhirtypes';
import {
  formatPractitionerStaffLabel,
  getPractitionerOrganizationRef,
} from '@unify/fhir-model/resources/practitioner';
import type { Role } from '../../lib/rbacRoles';
import {
  findCanonicalSuperviseeIds,
  isActivePractitioner,
} from '../../lib/supervision/supervisionRelationships';

export type CalendarAccessReason = 'self' | 'unify-admin' | 'practice-admin' | 'supervisor';

export type CalendarPractitionerAccess = {
  practitionerId: string;
  displayName: string;
  organizationId?: string;
  canView: boolean;
  canManage: boolean;
  accessReason: CalendarAccessReason;
};

const UNIFY_ADMIN_ROLES: ReadonlySet<Role> = new Set(['unify-admin', 'unify-assistant']);
const PRACTICE_ADMIN_ROLES: ReadonlySet<Role> = new Set([
  'practice-owner',
  'practice-admin',
  'practice-assistant',
]);

export function formatCalendarPractitionerName(practitioner: Practitioner): string {
  return formatPractitionerStaffLabel(practitioner) || practitioner.id || 'Unknown';
}

export function organizationIdFromPractitioner(practitioner: Practitioner): string | undefined {
  const ref = getPractitionerOrganizationRef(practitioner)?.reference;
  if (!ref?.startsWith('Organization/')) return undefined;
  return ref.slice('Organization/'.length) || undefined;
}

export function rolesGrantUnifyCalendarAdmin(roles: readonly Role[]): boolean {
  return roles.some((r) => UNIFY_ADMIN_ROLES.has(r));
}

export function rolesGrantPracticeCalendarAdmin(roles: readonly Role[]): boolean {
  return roles.some((r) => PRACTICE_ADMIN_ROLES.has(r));
}

export function rolesGrantSupervisorCalendarView(roles: readonly Role[]): boolean {
  return roles.includes('supervisor');
}

/** Session-type catalog scope for the viewer — wider for admin roles. */
export type CalendarSessionTypeScope = 'provider' | 'practice' | 'platform';

export function calendarSessionTypeScopeFromRoles(
  roles: readonly Role[],
): CalendarSessionTypeScope {
  if (rolesGrantUnifyCalendarAdmin(roles)) return 'platform';
  if (rolesGrantPracticeCalendarAdmin(roles)) return 'practice';
  return 'provider';
}

/** Rank for merging duplicate practitioner rows — higher wins. */
function accessRank(entry: CalendarPractitionerAccess): number {
  if (entry.canManage) {
    if (entry.accessReason === 'self') return 40;
    if (entry.accessReason === 'unify-admin') return 35;
    if (entry.accessReason === 'practice-admin') return 30;
    return 25;
  }
  return entry.accessReason === 'supervisor' ? 10 : 5;
}

function mergeAccess(
  map: Map<string, CalendarPractitionerAccess>,
  next: CalendarPractitionerAccess,
): void {
  const prev = map.get(next.practitionerId);
  if (!prev || accessRank(next) > accessRank(prev)) {
    map.set(next.practitionerId, next);
    return;
  }
  if (accessRank(next) === accessRank(prev) && next.canManage && !prev.canManage) {
    map.set(next.practitionerId, next);
  }
}

export interface ResolveCalendarPractitionerAccessArgs {
  currentPractitioner: Practitioner;
  /** Union of the user's role slugs (not display-role alone). */
  roles: readonly Role[];
  /** Candidate practitioners already loaded for the caller's scope. */
  candidates: readonly Practitioner[];
  /**
   * Organization id for practice-admin scoping. When omitted, practice-admin
   * grants no additional practitioners beyond self.
   */
  currentOrganizationId?: string;
  /**
   * Optional precomputed direct supervisee ids. When omitted, derived from
   * canonical supervising-practitioner extensions on `candidates` plus the
   * current practitioner's reverse list.
   */
  superviseeIds?: readonly string[];
}

/**
 * Resolve viewable/manageable practitioner calendars for the current user.
 * Always includes self. Deduplicates with highest canManage winning.
 */
export function resolveCalendarPractitionerAccess(
  args: ResolveCalendarPractitionerAccessArgs,
): CalendarPractitionerAccess[] {
  const { currentPractitioner, roles, candidates, currentOrganizationId } = args;
  const selfId = currentPractitioner.id;
  if (!selfId) return [];

  const byId = new Map<string, Practitioner>();
  for (const p of candidates) {
    if (p.id) byId.set(p.id, p);
  }
  byId.set(selfId, currentPractitioner);

  const map = new Map<string, CalendarPractitionerAccess>();

  const selfOrg = organizationIdFromPractitioner(currentPractitioner) ?? currentOrganizationId;
  mergeAccess(map, {
    practitionerId: selfId,
    displayName: formatCalendarPractitionerName(currentPractitioner),
    organizationId: selfOrg,
    canView: true,
    canManage: true,
    accessReason: 'self',
  });

  const unifyAdmin = rolesGrantUnifyCalendarAdmin(roles);
  const practiceAdmin = rolesGrantPracticeCalendarAdmin(roles);
  const supervisor = rolesGrantSupervisorCalendarView(roles);

  let superviseeIdSet: Set<string> | undefined;
  if (supervisor) {
    if (args.superviseeIds) {
      superviseeIdSet = new Set(args.superviseeIds);
    } else {
      superviseeIdSet = new Set(findCanonicalSuperviseeIds([...byId.values()], selfId));
    }
  }

  for (const practitioner of byId.values()) {
    const id = practitioner.id;
    if (!id || id === selfId) continue;
    if (!isActivePractitioner(practitioner)) continue;

    const organizationId = organizationIdFromPractitioner(practitioner);
    const displayName = formatCalendarPractitionerName(practitioner);

    if (unifyAdmin) {
      mergeAccess(map, {
        practitionerId: id,
        displayName,
        organizationId,
        canView: true,
        canManage: true,
        accessReason: 'unify-admin',
      });
      continue;
    }

    if (
      practiceAdmin &&
      currentOrganizationId &&
      organizationId === currentOrganizationId
    ) {
      mergeAccess(map, {
        practitionerId: id,
        displayName,
        organizationId,
        canView: true,
        canManage: true,
        accessReason: 'practice-admin',
      });
    }

    if (superviseeIdSet?.has(id)) {
      mergeAccess(map, {
        practitionerId: id,
        displayName,
        organizationId,
        canView: true,
        canManage: true,
        accessReason: 'supervisor',
      });
    }
  }

  const result = [...map.values()].filter((e) => e.canView);
  result.sort((a, b) => {
    if (a.practitionerId === selfId) return -1;
    if (b.practitionerId === selfId) return 1;
    return a.displayName.localeCompare(b.displayName);
  });
  return result;
}

/** Lookup canManage for an owner practitioner id from a resolved access list. */
export function canManagePractitionerCalendar(
  access: readonly CalendarPractitionerAccess[],
  ownerPractitionerId: string | undefined,
): boolean {
  if (!ownerPractitionerId) return false;
  return access.some((a) => a.practitionerId === ownerPractitionerId && a.canManage);
}
